import React, { useMemo, useState } from 'react';
import { Row } from 'reactstrap';
import { useQueryClient } from '@tanstack/react-query';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import { useAuthContext } from 'contexts/AuthContext';
import { IOptionStringValue } from 'bricks/types';
import { NSDropdown, NSLoadingScreen } from 'bricks';
import { PERIOD_OPTIONS } from 'components/ProFormaReturnsTable/constant';
import {
    ICreateReportPostData, IDeal, IDealSelection, IReportDetails, ReportTypeId,
} from 'views/Reports/types';
import {
    useCreateReport, useGetAggregateReportData, useGetOrgDealSummaries, useUpdateReport,
} from 'views/Reports/hooks';
import { URLS } from 'services/urls';
import useToast from 'hooks/useToast';
import { excelExport } from 'bricks/utils';
import ReportHeader from 'views/Reports/ReportHeader/ReportHeader';
import { isAllLandDevelopmentSelection } from 'views/Reports/helpers';
import DealSelection from 'views/Reports/components/DealSelection/DealSelection';
import PartnerReturnsTable from 'views/Reports/components/PartnerReturnsTable/PartnerReturnsTable';
import { useProFormaSocketContext } from 'views/ProFormaTable/components/ProForma/socketContext/ProFormaSocketProvider';
import { formatExcelExportData } from './helpers';
import AggregateProFormaReturnsTable from './AggregateProFormaReturnsTable/AggregateProFormaReturnsTable';
import { DealTimelineChart } from './DealTimelineChart';
import AggregateReportKeyStats from './AggregateReportKeyStats/AggregateReportKeyStats';

export interface IAggregateReportProps {
    report?: IReportDetails;
    onClickBack: () => void;
    isEditing?: boolean;
}

const AggregateReport = ({ report, onClickBack, isEditing = false }: IAggregateReportProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { showSuccess, showError } = useToast();
    const { selectedOrganizationId } = useAuthContext();
    const { operatingInfo } = useProFormaSocketContext();

    const [selectedPeriod, setSelectedPeriod] = React.useState<IOptionStringValue>(PERIOD_OPTIONS[0]);
    const [isEditMode, setIsEditMode] = useState(!report || isEditing);
    const [reportName, setReportName] = useState(report?.name || 'New Combined Deal Report');
    const [reportDescription, setReportDescription] = useState(report?.description || '');

    const { data: dealSummaries } = useGetOrgDealSummaries(selectedOrganizationId!, { enabled: true });
    // mapping to add dummy project and projectType, update when all values are available in db is ready
    const deals: IDeal[] = useMemo(() => (
        dealSummaries?.map(deal => ({
            id: deal.id!,
            dealName: deal.dealName!,
            dealType: deal.dealType!,
            assetType: deal.assetType,
            project: 'Project',
            projectType: 'Project Type',
            defaultProFormaId: deal.defaultProFormaId,
        })) || []
    ), [dealSummaries]);

    const reportDeals = report?.deals?.map(deal => ({
        deal: {
            id: deal.dealId,
            dealName: deal.dealName,
            dealType: deal.dealType,
            assetType: deal.assetType,
            project: 'Project',
            projectType: 'Project Type',
            defaultProFormaId: deal.proFormaId,
        },
        proFormaId: deal.proFormaId,
        proFormaModelId: deal.proFormaModelId,
        useProjectActuals: deal.useProjectActuals,
    })) || [];

    const [selectedDeals, setSelectedDeals] = useState<IDealSelection[]>(
        reportDeals.length > 0 ? reportDeals : (location.state?.selectedDeals || []),
    );

    const isLandDevelopment = isAllLandDevelopmentSelection(selectedDeals);

    const handlePeriodSelection = (option: IOptionStringValue) => {
        setSelectedPeriod(option);
    };

    const { data: aggReportData, isLoading } = useGetAggregateReportData({
        orgId: selectedOrganizationId!,
        proFormaIds: selectedDeals.map(selection => selection.proFormaId),
        actualsProFormaIds: selectedDeals
            .filter(selection => selection.useProjectActuals)
            .map(selection => selection.proFormaId),
    });

    const { mutate: createReport } = useCreateReport();
    const { mutate: updateReport } = useUpdateReport();

    const onClickExport = () => {
        if (report && aggReportData) {
            const formattedExcelExportData = formatExcelExportData(
                report.name,
                report.description,
                aggReportData,
                selectedPeriod.value,
                isLandDevelopment,
            );
            excelExport(report.name, false, formattedExcelExportData, true, []);
        }
    };

    const handleSaveReport = () => {
        const postData: ICreateReportPostData = {
            name: reportName,
            description: reportDescription,
            type: ReportTypeId.Aggregate,
            deals: selectedDeals.map(selection => ({
                dealId: selection.deal.id,
                proFormaId: selection.proFormaId,
                useProjectActuals: selection.useProjectActuals || false,
            })),
        };
        if (report) {
            updateReport(
                {
                    postData: {
                        report_id: report.id,
                        ...postData,
                    },
                    orgId: selectedOrganizationId!,
                },
                {
                    onSuccess: data => {
                        queryClient.invalidateQueries({ queryKey: ['reportDetails', data.id] });
                        showSuccess('Report updated successfully');
                        setIsEditMode(false);
                    },
                    onError: () => {
                        showError('Failed to update report. Please try again.');
                    },
                },
            );
        } else {
            createReport(
                {
                    postData,
                    orgId: selectedOrganizationId!,
                },
                {
                    onSuccess: data => {
                        const path = generatePath(URLS.REPORTS.REPORT_PAGE, { reportId: data.id });
                        showSuccess('Report created successfully');
                        navigate(path);
                    },
                    onError: () => {
                        showError('Failed to create report. Please try again.');
                    },
                },
            );
        }
    };

    const onClickCancel = () => {
        setIsEditMode(false);
        if (report) {
            setReportName(report.name);
            setReportDescription(report.description);
            setSelectedDeals(reportDeals || []);
        }
    };

    return (
        <div data-testid="aggregate-report-container">
            <ReportHeader
                reportId={report?.id}
                reportName={reportName}
                setReportName={setReportName}
                reportDescription={reportDescription}
                setReportDescription={setReportDescription}
                onClickBack={onClickBack}
                onClickExport={onClickExport}
                onClickSave={handleSaveReport}
                onClickCancel={onClickCancel}
                onClickEdit={() => setIsEditMode(true)}
                isEditing={isEditMode}
            />
            {isEditMode && (
                <>
                    <DealSelection
                        deals={deals}
                        titleSuffix="DEALS SELECTED"
                        selectedDeals={selectedDeals}
                        setSelectedDeals={setSelectedDeals}
                        showUseProjectActuals
                    />
                    <hr />
                </>
            )}
            {isLoading && <NSLoadingScreen />}
            {aggReportData && (
                <>
                    <AggregateReportKeyStats keyStats={aggReportData.keyStats} isLandDevelopment={isLandDevelopment} />
                    <Row className="d-flex justify-content-center mb-3">
                        <DealTimelineChart data={aggReportData.timelineData} />
                    </Row>
                    <hr />
                    <Row className="d-flex justify-content-between ml-1">
                        <h4 className="text-white">Property returns</h4>
                        <NSDropdown
                            containerClassName="mr-2"
                            options={PERIOD_OPTIONS}
                            selectedOption={selectedPeriod}
                            handleSelectOption={handlePeriodSelection}
                            placeholder="Select saved report"
                        />
                    </Row>
                    <AggregateProFormaReturnsTable
                        data={aggReportData.proFormaReturns}
                        operationBeginMonth={operatingInfo.operationBeginMonth || 0}
                        cadence={selectedPeriod.value}
                        isLandDevelopment={isLandDevelopment}
                    />
                    <hr className="mt-3" />
                    {aggReportData.partnerReturns && <PartnerReturnsTable data={aggReportData.partnerReturns} />}
                </>
            )}
        </div>
    );
};

export default AggregateReport;
